import { ChangeEvent, FormEvent, RefObject, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createBlogCategory, getBlogCategories, uploadImage } from '../api/client';
import { BlogCategory, BlogPostInfo } from '../api/types';
import remarkAlerts from '../blog/remarkAlerts';
import { Button } from './components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/dialog';
import { Input } from './components/input';
import { Label } from './components/label';
import { Switch } from './components/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/tabs';
import { Textarea } from './components/textarea';

export interface BlogPostFormValues {
  slug: string;
  title_en: string;
  title_tr: string;
  summary_en: string;
  summary_tr: string;
  content_en: string;
  content_tr: string;
  category: string;
  tags: string[];
  published: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: BlogPostInfo | null;
  onSubmit: (values: BlogPostFormValues) => Promise<void>;
}

const emptyValues: BlogPostFormValues = {
  slug: '',
  title_en: '',
  title_tr: '',
  summary_en: '',
  summary_tr: '',
  content_en: '',
  content_tr: '',
  category: 'project',
  tags: [],
  published: false,
};

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');

function fromInitial(initial?: BlogPostInfo | null): BlogPostFormValues {
  if (!initial) return emptyValues;
  return {
    slug: initial.slug,
    title_en: initial.title_en,
    title_tr: initial.title_tr,
    summary_en: initial.summary_en,
    summary_tr: initial.summary_tr,
    content_en: initial.content_en,
    content_tr: initial.content_tr,
    category: initial.category,
    tags: initial.tags,
    published: initial.is_published,
  };
}

function BlogPostForm({ open, onOpenChange, initial, onSubmit }: Props) {
  const [values, setValues] = useState<BlogPostFormValues>(fromInitial(initial));
  const [tagsInput, setTagsInput] = useState(initial?.tags.join(', ') ?? '');
  const [slugTouched, setSlugTouched] = useState(!!initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingLang, setUploadingLang] = useState<'en' | 'tr' | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryNameEn, setNewCategoryNameEn] = useState('');
  const [newCategoryNameTr, setNewCategoryNameTr] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const contentEnRef = useRef<HTMLTextAreaElement>(null);
  const contentTrRef = useRef<HTMLTextAreaElement>(null);
  const fileInputEnRef = useRef<HTMLInputElement>(null);
  const fileInputTrRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValues(fromInitial(initial));
      setTagsInput(initial?.tags.join(', ') ?? '');
      setSlugTouched(!!initial);
      setAddingCategory(false);
      setNewCategoryNameEn('');
      setNewCategoryNameTr('');
      getBlogCategories().then(setCategories).catch(() => setCategories([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = <K extends keyof BlogPostFormValues>(key: K, val: BlogPostFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleTitleEnChange = (val: string) => {
    set('title_en', val);
    if (!slugTouched) set('slug', slugify(val));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryNameEn.trim() || !newCategoryNameTr.trim()) return;
    setCreatingCategory(true);
    setError(null);
    try {
      const category = await createBlogCategory({
        slug: slugify(newCategoryNameEn),
        name_en: newCategoryNameEn.trim(),
        name_tr: newCategoryNameTr.trim(),
      });
      setCategories((prev) => [...prev, category]);
      set('category', category.slug);
      setAddingCategory(false);
      setNewCategoryNameEn('');
      setNewCategoryNameTr('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kategori oluşturulamadı');
    } finally {
      setCreatingCategory(false);
    }
  };

  const insertAtCursor = (
    ref: RefObject<HTMLTextAreaElement>,
    key: 'content_en' | 'content_tr',
    text: string
  ) => {
    const el = ref.current;
    const start = el?.selectionStart ?? Infinity;
    const end = el?.selectionEnd ?? Infinity;
    setValues((prev) => ({ ...prev, [key]: prev[key].slice(0, start) + text + prev[key].slice(end) }));
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + text.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleImageUpload = async (lang: 'en' | 'tr', e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLang(lang);
    setError(null);
    try {
      const { url } = await uploadImage(file);
      const ref = lang === 'en' ? contentEnRef : contentTrRef;
      const key = lang === 'en' ? 'content_en' : 'content_tr';
      insertAtCursor(ref, key, `![Görsel](${url})\n`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Görsel yüklenemedi');
    } finally {
      setUploadingLang(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      await onSubmit({ ...values, tags });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && !creatingCategory && uploadingLang === null && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? 'Yazıyı Düzenle' : 'Yeni Yazı'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="title_en">Başlık (EN)</Label>
              <Input id="title_en" value={values.title_en} onChange={(e) => handleTitleEnChange(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="title_tr">Başlık (TR)</Label>
              <Input id="title_tr" value={values.title_tr} onChange={(e) => set('title_tr', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={values.slug}
                onChange={(e) => { setSlugTouched(true); set('slug', e.target.value); }}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="category">Kategori</Label>
                <button
                  type="button"
                  onClick={() => setAddingCategory((v) => !v)}
                  className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-xs text-[hsl(var(--muted-foreground))] underline"
                >
                  {addingCategory ? 'Vazgeç' : '+ Yeni Kategori'}
                </button>
              </div>
              <select
                id="category"
                value={values.category}
                onChange={(e) => set('category', e.target.value)}
                className="flex h-9 w-full rounded-md border border-[hsl(var(--input))] bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--ring))]"
                required
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name_tr}</option>
                ))}
              </select>
            </div>
          </div>

          {addingCategory && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-[hsl(var(--border))] p-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new_category_en">Yeni Kategori (EN)</Label>
                <Input
                  id="new_category_en"
                  value={newCategoryNameEn}
                  onChange={(e) => setNewCategoryNameEn(e.target.value)}
                  placeholder="örn. Case Study"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new_category_tr">Yeni Kategori (TR)</Label>
                <Input
                  id="new_category_tr"
                  value={newCategoryNameTr}
                  onChange={(e) => setNewCategoryNameTr(e.target.value)}
                  placeholder="örn. Vaka İncelemesi"
                />
              </div>
              <div className="col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCreateCategory}
                  disabled={creatingCategory || !newCategoryNameEn.trim() || !newCategoryNameTr.trim()}
                >
                  {creatingCategory ? 'Ekleniyor...' : 'Kategoriyi Ekle'}
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="summary_en">Özet (EN)</Label>
              <Textarea id="summary_en" value={values.summary_en} onChange={(e) => set('summary_en', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="summary_tr">Özet (TR)</Label>
              <Textarea id="summary_tr" value={values.summary_tr} onChange={(e) => set('summary_tr', e.target.value)} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>İçerik (EN, Markdown)</Label>
              <div className="flex items-center gap-2">
                {uploadingLang === 'en' && <span className="text-xs text-[hsl(var(--muted-foreground))]">Yükleniyor...</span>}
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputEnRef.current?.click()} disabled={uploadingLang !== null}>
                  Görsel Ekle
                </Button>
                <input
                  ref={fileInputEnRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload('en', e)}
                />
              </div>
            </div>
            <Tabs defaultValue="write-en">
              <TabsList>
                <TabsTrigger value="write-en">Yaz</TabsTrigger>
                <TabsTrigger value="preview-en">Önizleme</TabsTrigger>
              </TabsList>
              <TabsContent value="write-en">
                <Textarea
                  ref={contentEnRef}
                  value={values.content_en}
                  onChange={(e) => set('content_en', e.target.value)}
                  className="min-h-[160px] font-mono text-xs"
                  required
                />
              </TabsContent>
              <TabsContent value="preview-en">
                <div className="max-h-[160px] overflow-y-auto rounded-md border border-[hsl(var(--border))] p-3">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkAlerts]}>{values.content_en || '*(boş)*'}</ReactMarkdown>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>İçerik (TR, Markdown — boş bırakılırsa EN gösterilir)</Label>
              <div className="flex items-center gap-2">
                {uploadingLang === 'tr' && <span className="text-xs text-[hsl(var(--muted-foreground))]">Yükleniyor...</span>}
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputTrRef.current?.click()} disabled={uploadingLang !== null}>
                  Görsel Ekle
                </Button>
                <input
                  ref={fileInputTrRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload('tr', e)}
                />
              </div>
            </div>
            <Tabs defaultValue="write-tr">
              <TabsList>
                <TabsTrigger value="write-tr">Yaz</TabsTrigger>
                <TabsTrigger value="preview-tr">Önizleme</TabsTrigger>
              </TabsList>
              <TabsContent value="write-tr">
                <Textarea
                  ref={contentTrRef}
                  value={values.content_tr}
                  onChange={(e) => set('content_tr', e.target.value)}
                  className="min-h-[160px] font-mono text-xs"
                />
              </TabsContent>
              <TabsContent value="preview-tr">
                <div className="max-h-[160px] overflow-y-auto rounded-md border border-[hsl(var(--border))] p-3">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkAlerts]}>{values.content_tr || '*(boş)*'}</ReactMarkdown>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tags">Etiketler (virgülle ayırın)</Label>
            <Input id="tags" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} />
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="published">Yayınla</Label>
            <Switch id="published" checked={values.published} onCheckedChange={(v) => set('published', v)} />
          </div>

          {error && <p className="text-sm text-[hsl(var(--destructive))]">{error}</p>}

          <Button type="submit" disabled={saving || uploadingLang !== null}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default BlogPostForm;
